import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  TextField,
} from "@mui/material";
import { api } from "../services/api";

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setMessage("Invalid reset link. Please request a new password reset.");
      return;
    }

    if (password !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setStatus("error");
      setMessage("Password must be at least 8 characters long");
      return;
    }

    try {
      setStatus("loading");
      await api.post("/auth/reset-password", {
        token,
        newPassword: password,
      });
      setStatus("success");
      setMessage("Password reset successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      setStatus("error");
      setMessage(
        "Failed to reset password. Please try again or request a new reset link.",
      );
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: "#9C27B0" }}
          >
            Expedient
          </Typography>
          <Typography variant="h5" component="h2" gutterBottom>
            Reset Your Password
          </Typography>
        </Box>

        {status === "success" && (
          <Alert severity="success" sx={{ width: "100%" }}>
            {message}
          </Alert>
        )}

        {status === "error" && (
          <Alert severity="error" sx={{ width: "100%" }}>
            {message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleResetPassword}
          sx={{ width: "100%" }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="New Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            disabled={status === "loading" || status === "success"}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(e.target.value)
            }
            disabled={status === "loading" || status === "success"}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: "#9C27B0",
              "&:hover": {
                backgroundColor: "#7B1FA2",
              },
            }}
            disabled={status === "loading" || status === "success"}
          >
            {status === "loading" ? "Resetting Password..." : "Reset Password"}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
